import { useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useUploadAssetMutation } from '../assets/assetsApi';
import { uploadAssetDeduplicated } from '../assets/assetUploadManager';
import { UploadedFile } from '../../components/template-editor/UploadsPanel';

interface UseClipboardImagePasteOptions {
  onImagePasted: (file: UploadedFile, dropX?: number, dropY?: number) => void;
  onInternalPaste?: () => void;
  isEnabled?: boolean;
}

/**
 * Checks if the user is currently typing inside an input, textarea, or contentEditable element
 */
const isEditableElement = (element: EventTarget | null): boolean => {
  if (!element || !(element instanceof HTMLElement)) return false;
  return (
    element.tagName === 'INPUT' ||
    element.tagName === 'TEXTAREA' ||
    element.isContentEditable ||
    Boolean(element.closest('[contenteditable="true"]')) ||
    Boolean(element.closest('input')) ||
    Boolean(element.closest('textarea'))
  );
};

/**
 * Checks if a string looks like an image URL, data URI, or CDN link
 */
const isLikelyImageUrl = (str: string): boolean => {
  if (!str) return false;
  const trimmed = str.trim();
  // Base64 data image
  if (/^data:image\/[a-z0-9.+_-]+;base64,/i.test(trimmed)) return true;
  // Blob URL
  if (/^blob:/i.test(trimmed)) return true;
  // HTTP/HTTPS URLs
  if (!/^https?:\/\//i.test(trimmed)) return false;
  // Common image file extensions (in path or query)
  if (/\.(png|jpe?g|webp|svg|gif|avif|bmp|ico|tiff?)(\?|#|$)/i.test(trimmed)) return true;
  // Query param indicating format (e.g. format=jpg, fm=png, etc.)
  if (/[?&](?:format|fm|ext|image|type)=(png|jpe?g|webp|svg|gif|avif)/i.test(trimmed)) return true;
  // Known image CDNs / photo domains
  if (/(?:images\.unsplash\.com|cdn\.pixabay\.com|images\.pexels\.com|i\.imgur\.com|media\.giphy\.com|upload\.wikimedia\.org|res\.cloudinary\.com|images\.ctfassets\.net)/i.test(trimmed)) return true;
  return false;
};

/**
 * Hook to listen for Ctrl+V / Cmd+V image paste events and place them directly on the canvas
 */
export function useClipboardImagePaste({ onImagePasted, onInternalPaste, isEnabled = true }: UseClipboardImagePasteOptions) {
  const [uploadAsset] = useUploadAssetMutation();

  useEffect(() => {
    if (!isEnabled) return;

    const handlePaste = async (e: ClipboardEvent) => {
      // 1. Guard against active text inputs
      if (isEditableElement(e.target)) {
        return;
      }

      const clipboardData = e.clipboardData;
      if (!clipboardData) {
        if (onInternalPaste) {
          e.preventDefault();
          onInternalPaste();
        }
        return;
      }

      // 2. Extract image file (Screenshots, Windows Snipping Tool, Win+V clipboard, copied image files, browser copy)
      let file: File | null = null;

      // Check items (standard in Chromium for copied images and screenshots)
      const items = Array.from(clipboardData.items || []);
      const imageItem = items.find(item => item.type && item.type.startsWith('image/'));
      if (imageItem) {
        file = imageItem.getAsFile();
      }

      // Check files (files copied from Windows File Explorer or dragged/pasted)
      if (!file) {
        const files = Array.from(clipboardData.files || []);
        const imageFile = files.find(f => (f.type && f.type.startsWith('image/')) || /\.(png|jpe?g|webp|svg|gif|avif|bmp|ico)$/i.test(f.name));
        if (imageFile) {
          file = imageFile;
        }
      }

      if (file) {
        e.preventDefault();
        e.stopPropagation();
        const toastId = toast.loading('Adding pasted image...');

        try {
          const localUrl = URL.createObjectURL(file);
          const tempId = `paste-${Date.now()}`;
          const safeName = file.name && file.name !== 'image.png'
            ? file.name
            : `Pasted_Image_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`;

          const uploadedFile: UploadedFile = {
            id: tempId,
            name: safeName,
            type: 'image',
            thumbnail: localUrl,
            uploadProgress: 100,
            status: 'completed',
            file,
            isLocal: true,
          };

          // Place onto canvas immediately
          onImagePasted(uploadedFile);

          // Asynchronously upload to backend asset storage with cryptographic deduplication (no duplicate AWS S3 uploads)
          uploadAssetDeduplicated(uploadAsset, file, safeName)
            .then((asset) => {
              if (asset?.url) {
                uploadedFile.thumbnail = asset.url;
                if ((uploadedFile as any).onAssetUploaded) {
                  (uploadedFile as any).onAssetUploaded(asset.url);
                }
              }
            })
            .catch((err) => {
              console.warn('[Clipboard] Deduplicated asset sync warning:', err);
            });

          toast.success('Image pasted onto canvas', { id: toastId });
        } catch (err) {
          console.error('[Clipboard] Failed to paste image:', err);
          toast.error('Failed to paste image', { id: toastId });
        }
        return;
      }

      // 3. Check for HTML or plain text image URLs copied from web pages
      const html = clipboardData.getData('text/html');
      const plainText = clipboardData.getData('text/plain')?.trim();

      let detectedUrl = '';
      if (html) {
        const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
        if (match && match[1]) {
          detectedUrl = match[1];
        }
      }

      if (!detectedUrl && plainText && isLikelyImageUrl(plainText)) {
        detectedUrl = plainText;
      }

      if (detectedUrl) {
        e.preventDefault();
        e.stopPropagation();
        const toastId = toast.loading('Fetching online image...');

        try {
          // Fetch the image as a Blob so it can be uploaded and rendered without CORS taint
          const response = await fetch(detectedUrl);
          const blob = await response.blob();

          if (!blob.type.startsWith('image/')) {
            throw new Error('Fetched URL is not an image');
          }

          const localUrl = URL.createObjectURL(blob);
          const tempId = `paste-url-${Date.now()}`;
          const fileName = `Online_Image_${Date.now()}.png`;
          const fileObj = new File([blob], fileName, { type: blob.type });

          const uploadedFile: UploadedFile = {
            id: tempId,
            name: fileName,
            type: 'image',
            thumbnail: localUrl,
            uploadProgress: 100,
            status: 'completed',
            file: fileObj,
            isLocal: true,
          };

          onImagePasted(uploadedFile);

          // Upload to persistent asset library with deduplication
          uploadAssetDeduplicated(uploadAsset, blob, fileName)
            .then((asset) => {
              if (asset?.url && (uploadedFile as any).onAssetUploaded) {
                (uploadedFile as any).onAssetUploaded(asset.url);
              }
            })
            .catch(() => {});

          toast.success('Online image added to canvas', { id: toastId });
        } catch (err) {
          console.warn('[Clipboard] Direct fetch failed (likely CORS), placing via direct URL:', err);
          // Fallback: Place using the direct URL
          const uploadedFile: UploadedFile = {
            id: `paste-url-${Date.now()}`,
            name: 'Web Image',
            type: 'image',
            thumbnail: detectedUrl,
            uploadProgress: 100,
            status: 'completed',
          };
          onImagePasted(uploadedFile);
          toast.success('Online image placed on canvas', { id: toastId });
        }
        return;
      }

      // 4. Fallback to internal layer paste if user previously copied a canvas element
      if (onInternalPaste) {
        e.preventDefault();
        onInternalPaste();
      }
    };

    window.addEventListener('paste', handlePaste, true);
    return () => {
      window.removeEventListener('paste', handlePaste, true);
    };
  }, [isEnabled, onImagePasted, onInternalPaste, uploadAsset]);
}
